import { MouseState, InteractionState } from '../types';
import { MouseButton } from '../types/enums';
import { logger } from '../utils/logger';

/** Minimum left-drag distance (px) before a background drag becomes a marquee. */
const MARQUEE_THRESHOLD = 3;

export class InteractionManager {
  private canvas: HTMLCanvasElement;
  private mouseState: MouseState;
  private onPan?: (deltaX: number, deltaY: number) => void;
  private onZoom?: (mouseX: number, mouseY: number, wheelDelta: number) => void;
  private onContextMenu?: (x: number, y: number) => void;
  private onWireCreationMove?: (x: number, y: number) => void;
  private onWireCreationClick?: (x: number, y: number) => void;
  private onWireCreationCancel?: () => void;
  private onSelectStart?: (x: number, y: number) => void;
  private onSelectMove?: (x: number, y: number) => void;
  private onSelectEnd?: (x: number, y: number) => void;
  private onSelectClear?: () => void;
  private isDraggingEntity: boolean = false;
  private interactionState: InteractionState = { mode: 'normal' };
  private wireCreationJustStarted: boolean = false;
  /** Canvas-relative point where the current left press began (marquee origin). */
  private marqueeStart = { x: 0, y: 0 };
  /** Most recent canvas-relative pointer position, used to finalize on mouseleave. */
  private lastCanvas = { x: 0, y: 0 };
  private isMarqueeing: boolean = false;

  /**
   * Bound DOM handlers stored once so the exact same references can be passed to
   * both addEventListener and removeEventListener; otherwise listeners can never
   * be detached and accumulate across remounts (causing duplicate pan/zoom).
   */
  private readonly handlers: Record<string, EventListener>;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.mouseState = {
      isMiddleDown: false,
      isLeftDown: false,
      lastX: 0,
      lastY: 0,
      isDraggingViewport: false
    };

    this.handlers = {
      mousedown: this.handleMouseDown.bind(this) as EventListener,
      mousemove: this.handleMouseMove.bind(this) as EventListener,
      mouseup: this.handleMouseUp.bind(this) as EventListener,
      wheel: this.handleWheel.bind(this) as EventListener,
      contextmenu: this.handleContextMenu.bind(this) as EventListener,
      mouseleave: this.handleMouseLeave.bind(this) as EventListener,
      auxclick: this.handleAuxClick.bind(this) as EventListener
    };

    this.setupEventListeners();
  }

  setCallbacks(callbacks: {
    onPan?: (deltaX: number, deltaY: number) => void;
    onZoom?: (mouseX: number, mouseY: number, wheelDelta: number) => void;
    onContextMenu?: (x: number, y: number) => void;
    onWireCreationMove?: (x: number, y: number) => void;
    onWireCreationClick?: (x: number, y: number) => void;
    onWireCreationCancel?: () => void;
    onSelectStart?: (x: number, y: number) => void;
    onSelectMove?: (x: number, y: number) => void;
    onSelectEnd?: (x: number, y: number) => void;
    onSelectClear?: () => void;
  }): void {
    this.onPan = callbacks.onPan;
    this.onZoom = callbacks.onZoom;
    this.onContextMenu = callbacks.onContextMenu;
    this.onWireCreationMove = callbacks.onWireCreationMove;
    this.onWireCreationClick = callbacks.onWireCreationClick;
    this.onWireCreationCancel = callbacks.onWireCreationCancel;
    this.onSelectStart = callbacks.onSelectStart;
    this.onSelectMove = callbacks.onSelectMove;
    this.onSelectEnd = callbacks.onSelectEnd;
    this.onSelectClear = callbacks.onSelectClear;
  }

  /** Converts a mouse event to canvas-relative coordinates. */
  private canvasCoords(event: MouseEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  setEntityDragging(isDragging: boolean): void {
    logger.interaction.debug(`setEntityDragging: ${isDragging}, viewport panning: ${this.mouseState.isDraggingViewport}`);
    this.isDraggingEntity = isDragging;
    
    // If entity dragging ended, stop viewport panning to prevent conflicts
    if (!isDragging && this.mouseState.isDraggingViewport) {
      this.mouseState.isDraggingViewport = false;
      this.canvas.style.cursor = 'default';
      logger.interaction.debug('Stopped viewport panning after entity drag ended');
    }
  }

  setInteractionState(state: InteractionState): void {
    const previousMode = this.interactionState.mode;
    this.interactionState = state;
    
    // If we just switched to wire creation mode, set flag to ignore immediate clicks
    if (previousMode !== 'wireCreation' && state.mode === 'wireCreation') {
      this.wireCreationJustStarted = true;
      setTimeout(() => {
        this.wireCreationJustStarted = false;
      }, 100); // 100ms delay to ignore the initiating click
    }
    
    // Update cursor based on mode
    switch (state.mode) {
      case 'wireCreation':
        this.canvas.style.cursor = 'crosshair';
        break;
      case 'normal':
      default:
        this.canvas.style.cursor = 'default';
        break;
    }
  }

  private setupEventListeners(): void {
    this.canvas.addEventListener('mousedown', this.handlers.mousedown);
    this.canvas.addEventListener('mousemove', this.handlers.mousemove);
    this.canvas.addEventListener('mouseup', this.handlers.mouseup);
    this.canvas.addEventListener('wheel', this.handlers.wheel, { passive: false });
    this.canvas.addEventListener('contextmenu', this.handlers.contextmenu);
    this.canvas.addEventListener('mouseleave', this.handlers.mouseleave);
    this.canvas.addEventListener('auxclick', this.handlers.auxclick);
  }

  private handleMouseDown(event: MouseEvent): void {
    // Handle wire creation mode
    if (this.interactionState.mode === 'wireCreation' && event.button === MouseButton.LEFT) {
      // Ignore clicks that happen immediately after starting wire creation (likely the button click)
      if (this.wireCreationJustStarted) {
        return;
      }
      
      event.preventDefault();
      if (this.onWireCreationClick) {
        const rect = this.canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        this.onWireCreationClick(mouseX, mouseY);
      }
      return;
    }
    
    // Handle wire creation mode - right click to cancel
    if (this.interactionState.mode === 'wireCreation' && event.button === MouseButton.RIGHT) {
      event.preventDefault();
      if (this.onWireCreationCancel) {
        this.onWireCreationCancel();
      }
      return;
    }
    
    this.mouseState.lastX = event.clientX;
    this.mouseState.lastY = event.clientY;
    
    if (event.button === MouseButton.MIDDLE) {
      event.preventDefault();
      this.mouseState.isMiddleDown = true;
      this.mouseState.isDraggingViewport = true;
      this.canvas.style.cursor = 'grabbing';
    } else if (event.button === MouseButton.LEFT) {
      // A left press on empty canvas begins a rubber-band selection (resolved in
      // mousemove once it passes the drag threshold); a press on an entity is
      // handled by that entity and flagged via setEntityDragging.
      this.mouseState.isLeftDown = true;
      this.isMarqueeing = false;
      this.marqueeStart = this.canvasCoords(event);
      this.lastCanvas = this.marqueeStart;
    }
  }

  private handleMouseMove(event: MouseEvent): void {
    // Handle wire creation mode
    if (this.interactionState.mode === 'wireCreation' && this.onWireCreationMove) {
      const { x, y } = this.canvasCoords(event);
      this.onWireCreationMove(x, y);
      return;
    }

    // Panning is reserved for the middle mouse button.
    if (this.mouseState.isMiddleDown && this.mouseState.isDraggingViewport && this.onPan) {
      const deltaX = event.clientX - this.mouseState.lastX;
      const deltaY = event.clientY - this.mouseState.lastY;
      this.onPan(deltaX, deltaY);
      this.mouseState.lastX = event.clientX;
      this.mouseState.lastY = event.clientY;
      return;
    }

    // A left drag on empty canvas (no entity grabbed) draws a selection marquee.
    if (this.mouseState.isLeftDown && !this.isDraggingEntity && this.interactionState.mode === 'normal') {
      const point = this.canvasCoords(event);
      this.lastCanvas = point;

      if (!this.isMarqueeing) {
        const movedFar =
          Math.abs(point.x - this.marqueeStart.x) >= MARQUEE_THRESHOLD ||
          Math.abs(point.y - this.marqueeStart.y) >= MARQUEE_THRESHOLD;
        if (!movedFar) {
          return;
        }
        this.isMarqueeing = true;
        this.onSelectStart?.(this.marqueeStart.x, this.marqueeStart.y);
      }
      this.onSelectMove?.(point.x, point.y);
    }
  }

  private handleMouseUp(event: MouseEvent): void {
    if (event.button === MouseButton.MIDDLE) {
      this.mouseState.isMiddleDown = false;
    } else if (event.button === MouseButton.LEFT) {
      if (this.isMarqueeing) {
        const { x, y } = this.canvasCoords(event);
        this.onSelectEnd?.(x, y);
      } else if (!this.isDraggingEntity && this.interactionState.mode === 'normal') {
        // A plain click on empty canvas clears the selection.
        this.onSelectClear?.();
      }
      this.isMarqueeing = false;
      this.mouseState.isLeftDown = false;
    }

    if (this.mouseState.isDraggingViewport) {
      this.mouseState.isDraggingViewport = false;
      this.canvas.style.cursor = 'default';
    }

    // Don't automatically reset entity dragging state here
    // Let the entity drag end events handle this explicitly
  }

  private handleWheel(event: WheelEvent): void {
    event.preventDefault();
    
    if (this.onZoom) {
      const rect = this.canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      this.onZoom(mouseX, mouseY, event.deltaY);
    }
  }

  private handleContextMenu(event: MouseEvent): void {
    event.preventDefault();
    
    if (this.onContextMenu) {
      this.onContextMenu(event.clientX, event.clientY);
    }
  }

  private handleMouseLeave(): void {
    // Finalize an in-progress marquee at the last known position so the box does
    // not linger when the pointer leaves the canvas mid-drag.
    if (this.isMarqueeing) {
      this.onSelectEnd?.(this.lastCanvas.x, this.lastCanvas.y);
      this.isMarqueeing = false;
    }
    this.mouseState.isDraggingViewport = false;
    this.mouseState.isMiddleDown = false;
    this.mouseState.isLeftDown = false;
    // Don't reset entity dragging state - entities handle their own drag state
    this.canvas.style.cursor = 'default';
  }

  private handleAuxClick(event: MouseEvent): void {
    if (event.button === MouseButton.MIDDLE) {
      event.preventDefault();
    }
  }

  destroy(): void {
    this.canvas.removeEventListener('mousedown', this.handlers.mousedown);
    this.canvas.removeEventListener('mousemove', this.handlers.mousemove);
    this.canvas.removeEventListener('mouseup', this.handlers.mouseup);
    this.canvas.removeEventListener('wheel', this.handlers.wheel);
    this.canvas.removeEventListener('contextmenu', this.handlers.contextmenu);
    this.canvas.removeEventListener('mouseleave', this.handlers.mouseleave);
    this.canvas.removeEventListener('auxclick', this.handlers.auxclick);
  }
}